import { setStyle, setChildren } from 'redom';
import { UiWaterMark } from './uiWaterMark';
import { ContentsManager } from '../Content/contentsManager';
import { IStudioOptions } from '../Viewer/interface';
import { svgStringToDom } from '../../Utils/html';
import {
    AR_ICON, FULLSCREEN_ICON, PAUSE_ICON, PLAY_ICON
} from './icons';

/**
 * @ignore Not useful for developpers,
 * Plus we want the menu to stays visible
 */

const fullScreenAvailable = () => (
    document.fullscreenEnabled
    || document.webkitFullscreenEnabled
    || document.mozFullscreenEnabled
    || document.msFullscreenEnabled
);

const fullScreenCurrentMode = () => (
    document.fullscreenElement
    || document.webkitFullscreenElement
    || document.mozFullScreenElement
    || document.msFullscreenElement
);

export class UiMenu extends UiWaterMark {
    constructor(parent: HTMLElement) {
        super(parent);

        this.addWaterMark();
        this.hideWaterMark();

        this.addFullScreenButton();
        this.addARButton();
        this.addPlayButton();
    }

    private contentsManager: ContentsManager

    public setContentsManager(contentsManager: ContentsManager): void {
        this.contentsManager = contentsManager;
    }

    private options: IStudioOptions = {}

    public checkOptions(options: IStudioOptions): void {
        this.options = options;
        if (options.fullScreen !== undefined) {
            if (options.fullScreen === true && fullScreenAvailable()) this.showFullScreen();
            else this.hideFullScreen();
        }
        if (options.waterMark || options.waterMark === undefined) this.showWaterMark();
        else this.hideWaterMark();
        if (options.AR) this.showAR();
        else this.hideAR();
        if (this.contentsManager) this.checkIfPlayable();
    }

    private goFullScreen() {
        // if supported
        if (fullScreenAvailable()) {
            // if already full screen; exit
            if (fullScreenCurrentMode()) {
                if (document.exitFullscreen) document.exitFullscreen();
                else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
                else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
                else if (document.msExitFullscreen) document.msExitFullscreen();
                // else go fullscreen
            } else {
                const element = this.parent;
                if (element.requestFullscreen) element.requestFullscreen();
                else if (element.mozRequestFullScreen) element.mozRequestFullScreen();
                else if (element.webkitRequestFullscreen) {
                    element.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
                } else if (element.msRequestFullscreen) element.msRequestFullscreen();
            }
        } else {
            this.hideFullScreen();
        }
    }

    private fullScreenButton: HTMLElement;

    private addFullScreenButton() {
        this.fullScreenButton = this.addButton(FULLSCREEN_ICON, 'Enter/Leave fullscreen mode', () => { this.goFullScreen(); });
        this.hideFullScreen();
    }

    private hideFullScreen() {
        if (this.fullScreenButton) setStyle(this.fullScreenButton, { display: 'none' });
    }

    private showFullScreen() {
        if (this.fullScreenButton) setStyle(this.fullScreenButton, { display: 'block' });
    }

    private playButton: HTMLElement;

    private addPlayButton() {
        this.playButton = this.addButton(PLAY_ICON, 'Play model animation', () => { this.checkPlay(); });
        this.hidePlay();
    }

    private hidePlay() {
        if (this.playButton) setStyle(this.playButton, { display: 'none' });
    }

    private showPlay() {
        if (this.playButton) setStyle(this.playButton, { display: 'block' });
    }

    private checkIfPlayable() {
        if (this.options.playable && this.isProjectPlayable()) this.showPlay();
        else this.hidePlay();
    }

    private isProjectPlayable(): boolean {
        return this.contentsManager.hasOnePlayableModel();
    }

    private playing = false

    private checkPlay() {
        setChildren(this.playButton, []);
        if (this.playing) {
            svgStringToDom(this.playButton, PLAY_ICON);
            this.contentsManager.pauseModelAnimations();
        } else {
            svgStringToDom(this.playButton, PAUSE_ICON);
            this.contentsManager.playModelAnimations();
        }
        this.playing = !this.playing;
    }

    private ARButton: HTMLElement;

    private addARButton() {
        this.ARButton = this.addButton(AR_ICON, 'See object in Augmented Reality', () => { this.goAR(); });
        this.hideAR();
    }

    private hideAR() {
        if (this.ARButton) setStyle(this.ARButton, { display: 'none' });
    }

    private showAR() {
        if (this.ARButton) setStyle(this.ARButton, { display: 'block' });
    }

    public onARRequested: () => void

    private goAR() {
        if (this.onARRequested) this.onARRequested();
    }

    public start(): void {
        this.checkIfPlayable();
        this.fadeIn();
    }
}
